<template>
    <div id="pleating-overview-page">
        <div class="card colors-list">
            <div class="card-content">
                <crm-table
                    v-if="initialFilters"
                    ref="pleatingOverviewTable"
                    :default-filters="initialFilters"
                    :columns="columns"
                    :items="products"
                    :count="products.length + 1"
                    :status="status"
                    :use-global-scroll="true"
                    @fetch="onFetchDebounced"
                    :get-row-style="getRowStyle"
                    :has-sticky="true"
                >
                    <template v-slot:no-results>
                        {{ trans('pages.pleating_overview.no_products_found') }}
                    </template>
                    <template v-slot:filter-clearFilters="props">
                        <ui5-button @click="clearFilters">
                            {{ trans('pages.pleating_overview.reset') }}
                        </ui5-button>
                    </template>
                    <template v-slot:filter-materialType="props">
                        <auto-complete-input
                            v-if="
                                materialTypeOptions &&
                                materialTypeOptions.length
                            "
                            class="filter"
                            :value="props.value"
                            @input="props.onInput(props.column.name, $event)"
                            :options="materialTypeOptions"
                            :placeholder="props.column.label"
                            :initial-search="getMaterialTypeName(props.value)"
                        />
                    </template>
                    <template v-slot:filter-productType="props">
                        <auto-complete-input
                            class="filter"
                            :value="props.value"
                            @input="props.onInput(props.column.name, $event)"
                            :options="productTypeOptions"
                            :placeholder="props.column.label"
                            :initial-search="getProductTypeName(props.value)"
                        />
                    </template>
                    <template v-slot:filter-flow="props">
                        <auto-complete-input
                            v-if="flowOptions && flowOptions.length"
                            class="filter"
                            :value="props.value"
                            @input="props.onInput(props.column.name, $event)"
                            :options="flowOptions"
                            :placeholder="props.column.label"
                        />
                    </template>
                    <template v-slot:filter-workingDaysUntilProduction="props">
                        <input
                            type="number"
                            min="1"
                            class="filter"
                            :value="props.value || 10"
                            @input="props.onInput(props.column.name, parseInt($event.target.value, 10) || 10)"
                            :placeholder="props.column.label"
                        />
                    </template>
                    <template v-slot:workingDaysUntilProduction="props">
                        <td>{{ props.item.workingDaysUntilProduction }}</td>
                    </template>
                    <template v-slot:customer="props">
                        <td>{{ props.item.customerName }}</td>
                    </template>
                    <template v-slot:selection="props">
                        <td>
                            <div class="checkbox">
                                <input
                                    v-model="props.item.selected"
                                    type="checkbox"
                                    :name="'selection-' + props.item.productId"
                                    :true-value="true"
                                    :false-value="false"
                                />
                            </div>
                        </td>
                    </template>
                    <template v-slot:actions="props">
                        <td>
                            <i
                                class="fa fa-info-circle back"
                                @click="goToProductPage(props.item.productId)"
                            />
                        </td>
                    </template>
                    <template v-slot:productType="props">
                        <td>
                            {{ props.item.producttypeName }}
                        </td>
                    </template>
                    <template v-slot:orderNr="props">
                        <td>
                            {{ props.item.orderId }}
                        </td>
                    </template>
                    <template
                        v-slot:footer
                        v-if="status === 'success' && products.length > 0"
                    >
                        <tr>
                            <th class="sticky">
                                <div class="multiselect-actions">
                                    <div class="checkbox">
                                        <input
                                            id="select-all"
                                            @change="selectAll"
                                            type="checkbox"
                                            name="select-all"
                                            :true-value="true"
                                            :false-value="false"
                                        />
                                    </div>
                                    <div
                                        @click="calculatePleatingLists"
                                        class="multiselect-action"
                                        :class="{ disabled: !hasSelection || isCalculating }"
                                    >
                                        <spinner v-if="isCalculating" />
                                        <i v-else class="fa fa-calculator" />
                                        {{
                                            trans(
                                                'pages.pleating_overview.calculate_pleating_orders'
                                            )
                                        }}
                                    </div>
                                    <div class="item-count">
                                        {{ trans('pages.pleating_overview.item_count') }}: {{ totalItemCount }}
                                    </div>
                                </div>
                            </th>
                        </tr>
                    </template>
                </crm-table>
            </div>
        </div>
    </div>
</template>

<script>
import Spinner from '../components/loader/Spinner';
import CrmTable from '../components/CrmTable';
import TextInput from '../components/TextInput';
import Ui5DatePickerStyled from '../components/Ui5DatePickerStyled.vue';
import AutoCompleteInput from '../components/AutoCompleteInput';
import { mapGetters } from 'vuex';
import { showErrorMessage, showInfoMessage } from '../dialogs';
import pleatingListApi from '../api/pleatingList';
import NavigationFunctionsMixin from '../mixins/navigation-functions';
import ErrorHelper from '../helpers/ErrorHelper';
import { clearedFilters } from '../shared/pleatingOverview';
import { colorArray } from '../shared/colors';
import localization from '../mixins/localization';
import orderApi from '../api/order';
import { getLabel } from '../shared/collections';

export default {
    mixins: [NavigationFunctionsMixin, localization],
    components: {
        Spinner,
        CrmTable,
        TextInput,
        AutoCompleteInput,
        Ui5DatePickerStyled,
    },
    data() {
        return {
            initialFilters: null,
            status: 'running',
            products: [],
            productTypes: null,
            optionFilters: null,
            isCalculating: false,
            columns: [
                {
                    name: 'selection',
                    label: '',
                },
                {
                    name: 'actions',
                    label: this.trans('pages.pleating_overview.actions'),
                },
                {
                    name: 'productNr',
                    label: this.trans('pages.pleating_overview.product_nr'),
                    searchable: false,
                    sortable: true,
                },
                {
                    name: 'orderNr',
                    label: this.trans('pages.pleating_overview.order_nr'),
                    searchable: true,
                    sortable: true,
                },
                {
                    name: 'productType',
                    label: this.trans('pages.pleating_overview.product_type'),
                    searchable: true,
                    sortable: true,
                },
                {
                    name: 'customer',
                    label: this.trans('pages.pleating_overview.customer'),
                    searchable: false,
                    sortable: true,
                },
                {
                    name: 'reference',
                    label: this.trans('pages.pleating_overview.reference'),
                    searchable: false,
                    sortable: true,
                },
                {
                    name: 'count',
                    label: this.trans('pages.edit_product.count'),
                    searchable: false,
                    sortable: true,
                },
                {
                    name: 'materialType',
                    label: this.trans('pages.pleating_overview.material_type'),
                    searchable: true,
                    sortable: true,
                },
                {
                    name: 'flow',
                    label: this.trans('pages.pleating_overview.flow'),
                    searchable: true,
                    sortable: true,
                },
                {
                    name: 'workingDaysUntilProduction',
                    label: this.trans(
                        'pages.pleating_overview.working_days_until_production'
                    ),
                    searchable: true,
                    sortable: false,
                },
                {
                    name: 'plateThickness',
                    label: this.trans(
                        'pages.pleating_overview.plate_thickness'
                    ),
                    searchable: true,
                    sortable: true,
                },
                {
                    name: 'productionDate',
                    label: this.trans(
                        'pages.pleating_overview.production_date'
                    ),
                    searchable: false,
                    sortable: true,
                },
                {
                    name: 'thickness',
                    label: this.trans('pages.pleating_overview.thickness'),
                    searchable: true,
                    sortable: true,
                },
                {
                    name: 'laserThickness',
                    label: this.trans(
                        'pages.pleating_overview.laser_thickness'
                    ),
                    searchable: true,
                    sortable: true,
                },
                {
                    name: 'overviewInfo',
                    label: this.trans('pages.pleating_overview.overview_info'),
                    searchable: false,
                    sortable: true,
                },
                {
                    name: 'clearFilters',
                    label: this.trans('pages.pleating_overview.clear_filters'),
                    searchable: true,
                    hideColumn: true,
                },
            ],
        };
    },
    computed: {
        ...mapGetters({
            hasRight: 'currentUser/hasRight',
        }),
        onFetchDebounced() {
            return _.debounce(this.onFetch, 500);
        },
        hasSelection() {
            return this.products.some((x) => x.selected);
        },
        totalItemCount() {
            const productsToCount = this.hasSelection
                ? this.products.filter((product) => product.selected)
                : this.products;

            return productsToCount.reduce((sum, product) => sum + (product.count || 0), 0);
        },
        materialTypeOptions() {
            const options = [
                {
                    value: null,
                    label: this.trans(
                        'pages.pleating_overview.all_material_types'
                    ),
                },
            ];

            if (this.optionFilters?.materialtypes) {
                this.optionFilters.materialtypes.forEach((materialTypeName) => {
                    options.push({
                        value: materialTypeName ?? 'db_null',
                        label:
                            materialTypeName ??
                            this.trans(
                                'pages.pleating_overview.no_material_type'
                            ),
                    });
                });
            }

            return options;
        },
        productTypeOptions() {
            const options = [
                {
                    value: '0',
                    label: this.trans(
                        'pages.product_edit_overview.all_product_types'
                    ),
                },
            ];
            if (this.productTypes && this.optionFilters?.productTypeIds) {
                this.productTypes.forEach((productType) => {
                    if (
                        this.optionFilters.productTypeIds.includes(
                            parseInt(productType.producttypeId)
                        )
                    ) {
                        options.push({
                            value: productType.producttypeId,
                            label: productType.label,
                        });
                    }
                });
            }

            return options;
        },
        flowOptions() {
            const options = [
                {
                    value: null,
                    label: this.trans('pages.pleating_overview.all_flows'),
                },
            ];
            if (this.optionFilters?.flows) {
                this.optionFilters.flows.forEach((flowName) => {
                    if (flowName) {
                        options.push({
                            value: flowName,
                            label: flowName,
                        });
                    }
                });
            }

            return options;
        },
    },
    methods: {
        getProductTypeName(id) {
            return getLabel(id, this.productTypeOptions);
        },
        getMaterialTypeName(id) {
            return getLabel(id, this.materialTypeOptions);
        },
        getRowStyle(product) {
            const bgColorIndex = Math.min(Math.max(product.daysPastDue, 0), 9);

            return {
                'background-color': colorArray[bgColorIndex],
                color:
                    bgColorIndex > 7 ? 'white !important' : 'black !important',
            };
        },
        clearFilters() {
            this.$refs.pleatingOverviewTable.originalFilters = {
                ...clearedFilters,
            };
            this.$refs.pleatingOverviewTable.clearFilters();
        },
        selectAll($event) {
            const value = $event.target.checked;
            this.products.forEach((x) => {
                x.selected = value;
            });
        },
        calculatePleatingLists() {
            if (this.isCalculating) return;

            this.isCalculating = true;
            const selectedProducts = this.products.filter((x) => x.selected);
            pleatingListApi
                .calculatePleating(selectedProducts.map((x) => x.productId))
                .then(() => {
                    showInfoMessage(
                        this.trans(
                            'pages.pleating_overview.pleating_orders_calculated',
                            {
                                products: selectedProducts
                                    .map((x) => x.productNr)
                                    .join(', '),
                            }
                        )
                    );
                    this.$refs.pleatingOverviewTable.fetch(false);
                })
                .catch((error) => {
                    showErrorMessage(
                        ErrorHelper.parseError(error.response.data)
                    );
                    this.$refs.pleatingOverviewTable.fetch(false);
                })
                .finally(() => {
                    this.isCalculating = false;
                });
        },
        onFetch(filters) {
            if (filters.page === 1) {
                this.products = [];
            }

            const payload = {
                ...filters,
                year: null,
                offset: this.products.length,
                limit: 50,
                sortOrder: filters.order,
                sortBy: filters.sort,
            };

            this.$store.commit(
                'pleatingOverview/setFilters',
                { ...payload },
                { root: true }
            );

            this.status = 'running';
            pleatingListApi
                .fetchOverview(payload)
                .then((response) => {
                    const products = response.data.data.results
                        .map((product) => ({
                            ...product,
                            flow: product.flowName,
                        }));
                    this.newCount = products.length;
                    const allProducts = [...this.products];
                    products.forEach((product) => {
                        product.selected = false;
                        if (
                            !allProducts.find(
                                (x) => x.productId == product.productId
                            )
                        ) {
                            allProducts.push(product);
                        }
                    });
                    this.products = allProducts;
                    this.optionFilters = response.data.data.optionFilters;
                    this.status = 'success';
                })
                .catch(() => {
                    if (!axios.isCancel(err)) {
                        showErrorMessage(
                            this.trans('pages.pleating_overview.unknown_error')
                        );
                        this.status = 'error';
                    }
                });
        },
    },
    mounted() {
        this.initialFilters =
            this.$store.getters['pleatingOverview/getFilters'];

        orderApi.productTypesForOrder().then((response) => {
            this.productTypes = response.data.data;
        });
    },
};
</script>
